import { zodResolver } from "@hookform/resolvers/zod";
import {
  ProjectsValidator,
  PROJECT_SUMMARY_MAX,
} from "Validators/Common/Project";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import * as z from "zod";

import AdminRoutes from "Routes/Components/Admin";

import { HTTPErrorCallback } from "Helpers/Error";
import { documentExtensions, imageExtensions } from "Helpers/File";
import { setAllFormValues } from "Helpers/Form";
import {
  currencyMask,
  defaultCurrencyMask,
  parseLocaleNumber,
  transformIntoArrayOptions,
} from "Helpers/Format";
import { locations } from "Helpers/Locations";
import { RELATED_AREA_OPTIONS, relatedAreasToOptions } from "Helpers/Project";
import { missingCityMessage } from "Helpers/RealizationAddress";

import UseFile from "Hooks/UseFile";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import Button from "Components/Admin/Buttons";
import FileUploader from "Components/Admin/FileUploader";
import FormWrapper from "Components/Admin/Form/FormWrapper";
import Input from "Components/Admin/Form/Input";
import Select from "Components/Admin/Form/Select";
import Section from "Components/Admin/Section";
import ToggleStatus from "Components/Admin/ToggleStatus";
import CharCounter from "Components/Global/Form/CharCounter";

import Checkboxes from "./Components/Checkboxes";
import Commitments from "./Components/Commitments";
import * as S from "./styles";

const UpdateProjectsForm = () => {
  const navigate = useNavigate();

  const { id } = useParams();
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { loading, setLoading } = UseGlobalLoading();
  const { uploadFile } = UseFile();

  const [selectedProject, setSelectedProject] = useState({});

  const [selectedCategories, setSelectedCategories] = useState([]);

  const [categoryOptions, setCategoryOptions] = useState([]);

  const [commitments, setCommitments] = useState([]);
  const [ageRange, setAgeRange] = useState([]);

  const [realizationAddress, setRealizationAddress] = useState({});
  // Estado do seletor de UF do editor de "Locais de Realização".
  const [currentUf, setCurrentUf] = useState(null);

  const [srcData, setSrcData] = useState({});

  // Capa do projeto (projects.project_image). Uma imagem só.
  // `coverImage`: [] | [File] (nova) | [{ file_src, file_name }] (a que veio do banco).
  // `coverChanged`: a chave só entra no payload quando o admin mexe no campo —
  // reenviar o que está na tela gravaria a URL ASSINADA (que expira) na coluna.
  const [coverImage, setCoverImage] = useState([]);
  const [coverChanged, setCoverChanged] = useState(false);

  const [totalAuthorizedValue, setTotalAuthorizedValue] = useState();

  const [captureBalance, setCaptureBalance] = useState();

  const [selectedRelatedAreas, setSelectedRelatedAreas] = useState([]);

  useEffect(() => {
    if (id) {
      return getProductions(id);
    }

    // eslint-disable-next-line
  }, [id]);

  const formSchema = z.object({
    responsible_name: ProjectsValidator.responsible_name,
    email: ProjectsValidator.email,
    role: ProjectsValidator.role,
    address: ProjectsValidator.address,
    neighbourhood: ProjectsValidator.neighbourhood,
    city: ProjectsValidator.city,
    state: ProjectsValidator.state,
    cep: ProjectsValidator.cep,
    phone: ProjectsValidator.phone,
    organization_type: ProjectsValidator.organization_type,
    organization_area: ProjectsValidator.organization_area,
    institution_history: ProjectsValidator.institution_history,
    project_name: ProjectsValidator.project_name,
    project_summary: ProjectsValidator.project_summary,
    project_modality: ProjectsValidator.project_modality,
    main_goal: ProjectsValidator.main_goal,
    project_start_at: ProjectsValidator.project_start_at,
    project_finish_at: ProjectsValidator.project_finish_at,
    project_direct_public_number:
      ProjectsValidator.project_direct_public_number,
    project_commitments: z.string().array().optional().nullable(),
    project_age_range: z.string().array().optional().nullable(),
  });

  const methods = useForm({
    resolver: zodResolver(formSchema),
  });
  const { register, setValue, watch } = methods;

  const getProductions = (id) => {
    api
      .post("/projects/get-project-by-id", { project_id: id })
      .then((response) => {
        const project = response.data;
        setSelectedProject(project);

        setAllFormValues(
          {
            ...project,
            project_links: project.project_links?.join(", "),
          },
          setValue,
          true,
        );

        getCategories(project?.categories);

        setCommitments(project.project_commitments);
        setAgeRange(project.project_age_range);

        setTotalAuthorizedValue(
          defaultCurrencyMask(project.total_authorized_value),
        );

        setCaptureBalance(defaultCurrencyMask(project.capture_balance));

        setSelectedRelatedAreas(relatedAreasToOptions(project.related_areas));

        const parseJsonField = (field) => {
          try {
            return JSON.parse(field);
          } catch (error) {
            return field;
          }
        };

        if (project?.realization_address)
          setRealizationAddress(parseJsonField(project?.realization_address));

        const srcObj = {
          cnpj_src: project.cnpj_src,
          approval_src: project.approval_src,
          registered_project_src: project.registered_project_src,
          project_budget_src: project.project_budget_src,
          caption_book_src: project.caption_book_src,
          other_src: project.other_src,
        };

        setSrcData(srcObj);

        // A capa chega do backend como URL assinada (só para exibição).
        setCoverImage(
          project.project_image
            ? [
                {
                  file_src: project.project_image,
                  file_name: "Capa do projeto",
                },
              ]
            : [],
        );
        setCoverChanged(false);
      })
      .catch((error) => {
        return HTTPErrorCallback(error);
      });
  };

  const getCategories = async (categories) => {
    setLoading(true);

    try {
      const response = await api.get("/projects/get-project-categories");

      const options = transformIntoArrayOptions(
        response.data,
        "id",
        "display_name",
      );

      setCategoryOptions(options);

      setSelectedCategories(
        options.filter((option) => categories.includes(option.value)),
      );

      setLoading(false);
    } catch (error) {
      setLoading(false);
      HTTPErrorCallback(error);
    }
  };

  // Selecionar e remover (o "X" do próprio FileUploader) passam por aqui — as
  // duas ações precisam marcar que a capa mudou.
  const handleCoverChange = (files) => {
    setCoverImage(files || []);
    setCoverChanged(true);
  };

  // "Documento de apresentação" = anexo "Book de captação". Vem de
  // get-project-by-id como array de ProjectFile, com `file_src` já assinado.
  const captionBookSrc = srcData?.caption_book_src?.[0]?.file_src || null;

  // --- Editor de "Locais de Realização" ({ "Estado": [cidades] }) ---
  // A chave é o NOME do estado (ex.: "Alagoas"); array vazio = todo o estado.
  const ufOptions = locations.states.map((state) => ({
    label: state.name,
    value: state.name,
  }));

  const currentCityOptions = currentUf
    ? (
        locations.states.find((state) => state.name === currentUf.value)
          ?.cities || []
      ).map((city) => ({ label: city, value: city }))
    : [];

  const handleSelectUf = (option) => {
    setCurrentUf(option);
    // Adiciona o estado (todo o estado) se ainda não estiver na lista.
    if (option && realizationAddress[option.value] === undefined)
      setRealizationAddress((prev) => ({ ...prev, [option.value]: [] }));
  };

  const handleSelectCities = (selected) => {
    if (!currentUf) return;
    setRealizationAddress((prev) => ({
      ...prev,
      [currentUf.value]: (selected || []).map((option) => option.value),
    }));
  };

  const removeUf = (uf) => {
    setRealizationAddress((prev) => {
      const next = { ...prev };
      delete next[uf];
      return next;
    });
    if (currentUf?.value === uf) setCurrentUf(null);
  };

  const removeCity = (uf, city) => {
    setRealizationAddress((prev) => ({
      ...prev,
      [uf]: (prev[uf] || []).filter((item) => item !== city),
    }));
  };

  const submit = async (data) => {
    setLoading(true);

    try {
      data.project_commitments = commitments;
      data.project_age_range = ageRange;

      // Arquivos novos (instâncias de File) são enviados ao S3; os já existentes
      // (com `id`) são preservados para o backend não recriá-los.
      for (const key in srcData) {
        if (
          srcData[key] &&
          Array.isArray(srcData[key]) &&
          srcData[key].length
        ) {
          for (const file of srcData[key]) {
            if (!data[key]) data[key] = [];

            if (file?.id) {
              data[key].push(file);
              continue;
            }

            const file_src = await uploadFile(file);

            data[key].push({
              file_name: file.name,
              file_src,
            });
          }
        }
      }

      // Capa: só entra no payload se o admin mexeu no campo. Ausente = manter a
      // atual; null = remover.
      if (coverChanged) {
        const cover = coverImage[0];

        data.project_image =
          cover instanceof File ? await uploadFile(cover) : null;
      }

      data.total_authorized_value = parseLocaleNumber(totalAuthorizedValue);

      data.capture_balance = parseLocaleNumber(captureBalance);

      data.related_areas = selectedRelatedAreas.map((area) => area.value);

      // Locais de realização: enviado como JSON string ({ "Estado": [cidades] });
      // vazio => null para limpar. O backend armazena como JSON.
      //
      // Toda UF precisa de cidade: escolher o estado aqui já o insere com a
      // lista VAZIA, então é justamente por esta tela que "todo o estado" entrava
      // no cadastro. O backend recusa igual; a checagem local evita a ida e volta
      // e nomeia os estados pendentes.
      const missingCity = missingCityMessage(realizationAddress);

      if (missingCity) {
        setLoading(false);
        showNotification("danger", "Atenção!", missingCity);
        return;
      }

      data.realization_address = Object.keys(realizationAddress).length
        ? JSON.stringify(realizationAddress)
        : null;

      await api.post("/projects/update-project", {
        project_id: selectedProject.id,
        ...data,
      });

      setLoading(false);

      showNotification(
        "success",
        "Sucesso!",
        "Projeto atualizado com sucesso!",
      );
    } catch (error) {
      setLoading(false);
      return HTTPErrorCallback(error);
    }
  };

  return (
    <Section>
      <S.Wrapper>
        <S.Buttons>
          <Button
            type="button"
            text="Voltar"
            disabled={loading}
            onClick={() => navigate(AdminRoutes.ListProjects.path)}
            width="30%"
          />

          <ToggleStatus
            route={"/projects/toggle-project-status/"}
            item={selectedProject}
            refresh={() => getProductions(id)}
          />
        </S.Buttons>
        <FormWrapper {...methods} submit={submit}>
          <Input
            {...register("responsible_name")}
            type="text"
            label="Nome do Responsável"
            placeholder="Nome do Responsável"
          />
          <Input
            {...register("email")}
            type="email"
            label="E-mail"
            placeholder="E-mail"
          />
          <Input
            {...register("role")}
            type="text"
            label="Função"
            placeholder="Função"
          />
          <Input
            {...register("cep")}
            type="text"
            label="CEP"
            placeholder="CEP"
          />
          <Input
            {...register("state")}
            type="text"
            label="Estado"
            placeholder="Estado"
          />
          <Input
            {...register("neighbourhood")}
            type="text"
            label="Bairro"
            placeholder="Bairro"
          />
          <Input
            {...register("city")}
            type="text"
            label="Cidade"
            placeholder="Cidade"
          />
          <Input
            {...register("address")}
            type="text"
            label="Endereço"
            placeholder="Endereço"
          />
          <Input
            {...register("phone")}
            type="text"
            label="Telefone"
            placeholder="Telefone"
          />
          <Input
            {...register("organization_type")}
            type="text"
            label="Tipo de Organização"
            placeholder="Tipo de Organização"
          />
          <Input
            {...register("organization_area")}
            type="text"
            label="Área de Atuação da Organização"
            placeholder="Área de Atuação da Organização"
          />
          <Input
            {...register("institution_history")}
            type="text"
            label="Histórico da Instituição"
            placeholder="Histórico da Instituição"
          />
          <Input
            {...register("project_name")}
            type="text"
            label="Nome do Projeto"
            placeholder="Nome do Projeto"
          />
          <Input
            {...register("project_summary")}
            type="text"
            label="Resumo do Projeto"
            placeholder="Resumo do Projeto"
            maxLength={PROJECT_SUMMARY_MAX}
          />
          <CharCounter
            value={watch("project_summary")}
            max={PROJECT_SUMMARY_MAX}
            variant="light"
          />
          <Select
            name="project_modality"
            label="Lei de Incentivo que o projeto está aprovado e/ou apto a captação:"
            placeholder="Digite aqui"
            options={categoryOptions}
            value={selectedCategories}
            onChange={(e) => setSelectedCategories(e)}
            required
            isMulti
          />
          <Input
            {...register("main_goal")}
            type="text"
            label="Objetivo Principal"
            placeholder="Objetivo Principal"
          />
          <S.Row>
            <Input
              {...register("project_start_at")}
              type="date"
              label="Data de Início do Projeto"
              placeholder="Data de Início do Projeto"
            />
            <Input
              {...register("project_finish_at")}
              type="date"
              label="Data de Término do Projeto"
              placeholder="Data de Término do Projeto"
            />
          </S.Row>
          <Commitments
            commitments={commitments}
            setCommitments={setCommitments}
          />
          <Checkboxes
            label="Qual(is) a(s) faixa(s) etária(s) do(s) beneficiário(s) final(is) do projeto? "
            items={[
              { name: "childs", label: "Crianças" },
              { name: "adults", label: "Adulto" },
              { name: "teenagers", label: "Adolescentes" },
              { name: "elderly", label: "Idoso" },
              { name: "youngsters", label: "Jovens" },
              { name: "all", label: "Todos" },
            ]}
            selected={ageRange}
            setSelected={setAgeRange}
          />
          <Input
            {...register("project_direct_public_number")}
            type="number"
            label="Número Direto de Público do Projeto"
            placeholder="Número Direto de Público do Projeto"
          />

          <S.Info>
            <span>Locais de Realização</span>

            <Select
              name="realization_uf"
              label="Estado"
              placeholder="Selecione o estado"
              options={ufOptions}
              value={currentUf}
              onChange={handleSelectUf}
            />

            {currentUf && (
              <Select
                name="realization_cities"
                label={`Cidades em ${currentUf.value} (deixe vazio para todo o estado)`}
                placeholder="Selecione as cidades"
                options={currentCityOptions}
                value={(realizationAddress[currentUf.value] || []).map(
                  (city) => ({ label: city, value: city }),
                )}
                onChange={handleSelectCities}
                isMulti
              />
            )}

            {Object.keys(realizationAddress).map((uf) => (
              <S.Uf key={uf}>
                <h3>
                  {uf}
                  <S.RemoveButton type="button" onClick={() => removeUf(uf)}>
                    remover estado
                  </S.RemoveButton>
                </h3>
                <S.Cities>
                  {realizationAddress[uf].length === 0 ? (
                    <S.City>Todo o estado</S.City>
                  ) : (
                    realizationAddress[uf].map((city) => (
                      <S.City key={city}>
                        {city}
                        <S.RemoveCity
                          type="button"
                          onClick={() => removeCity(uf, city)}
                          aria-label={`Remover ${city}`}
                        >
                          ×
                        </S.RemoveCity>
                      </S.City>
                    ))
                  )}
                </S.Cities>
              </S.Uf>
            ))}
          </S.Info>

          <Input
            {...register("total_authorized_value")}
            type="string"
            label="Valor Total Autorizado"
            placeholder="Valor Total Autorizado"
            value={totalAuthorizedValue}
            onChange={(e) =>
              setTotalAuthorizedValue(currencyMask(e.target.value))
            }
          />

          <Input
            {...register("capture_balance")}
            type="string"
            label="Saldo disponível a captar"
            placeholder="Saldo disponível a captar"
            value={captureBalance}
            onChange={(e) => setCaptureBalance(currencyMask(e.target.value))}
          />

          <Select
            name="related_areas"
            label="Área relacionada"
            placeholder="Selecione"
            options={RELATED_AREA_OPTIONS}
            value={selectedRelatedAreas}
            onChange={(e) => setSelectedRelatedAreas(e)}
            isMulti
          />

          <S.CoverField>
            <S.CoverLabel>
              Anexe a imagem da capa do projeto
              {captionBookSrc ? (
                <S.TooltipLink
                  href={captionBookSrc}
                  target="_blank"
                  rel="noreferrer"
                  title="Obtido a partir do documento de apresentação"
                >
                  ?
                </S.TooltipLink>
              ) : (
                <S.TooltipIcon title="Obtido a partir do documento de apresentação">
                  ?
                </S.TooltipIcon>
              )}
            </S.CoverLabel>

            <FileUploader
              label=""
              loading={loading}
              allowedExtensions={imageExtensions}
              setFiles={handleCoverChange}
              files={coverImage}
              showFileName
            />
          </S.CoverField>

          <FileUploader
            label="Cartão de CNPJ"
            loading={loading}
            allowedExtensions={[...imageExtensions, ...documentExtensions]}
            setFiles={(e) => setSrcData({ ...srcData, cnpj_src: e })}
            files={srcData?.cnpj_src || []}
            allowMultiple
          />
          <FileUploader
            label="Documento de aprovação do projeto (DOU - APROVAÇÃO / PRORROGAÇÃO)"
            loading={loading}
            allowedExtensions={[...imageExtensions, ...documentExtensions]}
            setFiles={(e) => setSrcData({ ...srcData, approval_src: e })}
            files={srcData?.approval_src || []}
            allowMultiple
          />
          <FileUploader
            label="Projeto conforme inscrito na lei"
            loading={loading}
            allowedExtensions={[...imageExtensions, ...documentExtensions]}
            setFiles={(e) =>
              setSrcData({ ...srcData, registered_project_src: e })
            }
            files={srcData?.registered_project_src || []}
            allowMultiple
          />
          <FileUploader
            label="Planilha orçamentária do projeto"
            loading={loading}
            allowedExtensions={[...imageExtensions, ...documentExtensions]}
            setFiles={(e) => setSrcData({ ...srcData, project_budget_src: e })}
            files={srcData?.project_budget_src || []}
            allowMultiple
          />
          <FileUploader
            label="Book de captação"
            loading={loading}
            allowedExtensions={[...imageExtensions, ...documentExtensions]}
            setFiles={(e) => setSrcData({ ...srcData, caption_book_src: e })}
            files={srcData?.caption_book_src || []}
            allowMultiple
          />

          <FileUploader
            label="Outros documentos"
            loading={loading}
            allowedExtensions={[...imageExtensions, ...documentExtensions]}
            setFiles={(e) => setSrcData({ ...srcData, other_src: e })}
            files={srcData?.other_src || []}
            allowMultiple
          />

          <Button type="submit" text="Atualizar" disabled={loading} />
        </FormWrapper>
      </S.Wrapper>
    </Section>
  );
};

export default UpdateProjectsForm;
